<template>
    <div class="page-height-controller-modern q-px-md q-py-sm stats-page">
        <div class="q-gutter-sm">
            <q-checkbox
                v-for="st in state.sysTemplates"
                :key="st.id"
                v-model="state.sysTemplateIds"
                :val="st.id"
                :label="st.name"
                color="primary"
            />
        </div>
        <div class="stats-page-grid">
            <div class="chart">
                <AgCharts v-if="organizationChartOptions" :options="organizationChartOptions" />
                <q-skeleton v-else-if="loading" height="100%" type="rect" />
                <div v-else>Нет данных для отображения</div>
            </div>
            <div class="chart">
                <AgCharts v-if="userChartOptions" :options="userChartOptions" />
                <q-skeleton v-else-if="loading" height="100%" type="rect" />
                <div v-else>Нет данных для отображения</div>
            </div>
            <div class="chart size-2">
                <AgCharts v-if="activeUsersOverTimeChartOptions" :options="activeUsersOverTimeChartOptions" />
                <q-skeleton v-else-if="loading" height="100%" type="rect" />
                <div v-else>Нет данных для отображения</div>
            </div>
        </div>

        <!--        <pre style="grid-column: span 4">{{ JSON.stringify(stats, null, 4) }}</pre>-->
    </div>
</template>

<script setup lang="ts">
import { useRequestWrapper } from '@platform/shared/hooks/use-request-wrapper';
import { computed, onBeforeMount, reactive, watch } from 'vue';
import { NodeAPI } from '@platform/shared/rpc';
import { AgCharts } from 'ag-charts-vue3';
import type { AgChartOptions } from 'ag-charts-enterprise';
import { time } from 'ag-charts-enterprise';
import '@managing/utils/ag-charts-ee';
import { formatTStoDATE } from '@platform/shared/lib/datetime';

const state = reactive({
    sysTemplates: [] as { id: number; name: string }[],
    sysTemplateIds: [] as number[],
});
const stats = reactive({
    organizations: null as null | Record<'all' | 'disabled' | 'active', number>,
    users: null as null | Record<'all' | 'disabled' | 'active' | 'online', number>,
    activeUsersOverTime: null as null | { date_marker: string; active_users: number }[],
});
const { loading, handler: reloadStats } = useRequestWrapper(async () => {
    Object.assign(
        stats,
        await NodeAPI.SuperAdminsService.getStats({
            body: { sysSystemTemplateIds: [...state.sysTemplateIds] },
        })
    );
});

const { loading: templatesLoading, handler: loadTemplates } = useRequestWrapper(async () => {
    const templateList = (await NodeAPI.Rpc.readWithRefs<any>('platform.sys_system_template', {
        withViewedInfo: false,
        withFilesInfo: false,
        rowStatuses: false,
        sort: ['id'],
    })) as { id: number; name: string }[];
    state.sysTemplates = templateList;
});

onBeforeMount(async () => {
    await loadTemplates();
    state.sysTemplateIds = [1, 2].filter((id) => state.sysTemplates.some((st) => st.id === id));
});

watch(() => state.sysTemplateIds, reloadStats);

const chartDefaults: AgChartOptions = {
    animation: {
        enabled: true,
    },
    series: [
        {
            type: 'donut',
            calloutLabelKey: 'label',
            angleKey: 'value',
            innerRadiusRatio: 0.7,
            itemStyler: (params) => {
                return { fill: params.datum.fill };
            },
            cursor: 'pointer',
            listeners: {
                nodeClick: (params) => {
                    console.log('nodeClick', params);
                },
            },
        },
    ],
};

function mapStats(stat: NonNullable<(typeof stats)['users' | 'organizations']>) {
    const data = {
        disabled: {
            value: stat.disabled,
            label: 'Отключенные',
            fill: '#79828f',
        },
        active: {
            value: stat.active,
            label: 'Активные *',
            fill: '#00ba5a',
        },
        inactive: {
            value: stat.all - (stat.disabled + stat.active),
            label: 'Неактивные',
            fill: '#b0b03e',
        },
    };
    return Object.entries(data).map(([key, obj]) => ({ ...obj, key }));
}

const organizationChartOptions = computed(() => {
    if (!stats.organizations) return null;
    return {
        ...chartDefaults,
        data: mapStats(stats.organizations),
        title: { text: 'Организации' },
    } satisfies AgChartOptions;
});

const userChartOptions = computed(() => {
    if (!stats.users) return null;
    return {
        ...chartDefaults,
        data: mapStats(stats.users),
        title: { text: 'Пользователи' },
        subtitle: {
            enabled: true,
            text: `Онлайн: ${stats.users.online}`,
        },
    } satisfies AgChartOptions;
});

const activeUsersOverTimeChartOptions = computed(() => {
    if (!stats.activeUsersOverTime) return null;
    const options: AgChartOptions = {
        ...chartDefaults,
        data: stats.activeUsersOverTime.map((item) => ({ ...item, date: new Date(item.date_marker) })),
        series: [
            {
                type: 'line',
                xKey: 'date',
                yKey: 'active_users',
                stroke: '#00ba5a',
                marker: {
                    enabled: true,
                    shape: 'circle',
                    size: 2,
                    fill: '#00ba5a',
                    stroke: '#00ba5a',
                },
            },
        ],
        axes: [
            {
                type: 'time',
                nice: false,
                position: 'bottom',
                interval: { step: time.monday, minSpacing: 360 },
                label: {
                    formatter: (params) => {
                        return formatTStoDATE(params.value, 'D.MM') || '';
                    },
                    avoidCollisions: true,
                },
            },
            {
                type: 'number',
                position: 'left',
            },
        ],
        title: { text: 'Включенные пользователи по времени' },
    };
    return options;
});
</script>

<style scoped lang="scss">
.stats-page .stats-page-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 400px));
    grid-template-rows: auto 1fr;
    gap: 12px;

    .chart {
        aspect-ratio: 1;
    }

    .chart.size-2 {
        aspect-ratio: 2;
        grid-column: span 2;
    }
}
</style>
